import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchSession } from './api'
import { isApiError } from '@/shared/api/problem'
import {
  SESSION_STALE_TIME_MS,
  SessionContext,
  sessionQueryKey,
  type SessionStatus,
} from './session-context'

/**
 * Session 유효성을 확인 엔드포인트 하나로 판정하고 그 응답에 실린 화면 설정을 함께 받는다.
 *
 * 401이 오면 미인증이고 그 밖의 실패는 오류다. 둘을 뭉개면 서버가 멎은 것을 사용자가 자기
 * Session이 끊긴 것으로 읽는다.
 *
 * 신선도를 1분으로 둔다. 확인이 도는 자리가 셋이라 그보다 짧으면 화면을 몇 번 옮기는 동안
 * 요청이 그만큼 나간다. 창이 앞으로 나올 때 다시 묻는 것도 이 신선도가 함께 조인다.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: sessionQueryKey,
    queryFn: ({ signal }) => fetchSession(signal),
    retry: false,
    staleTime: SESSION_STALE_TIME_MS,
    refetchOnWindowFocus: true,
  })

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: sessionQueryKey })
  }, [queryClient])

  /*
   * 살아 있던 Session이 끊긴 것과 처음부터 없던 것을 가른다. 앞의 것만 로그인 화면에서 알린다.
   * 한 번 참이 되면 되돌리지 않는다. 이 방문에서 인증을 지난 적이 있다는 사실은 변하지 않는다.
   */
  const [wasAuthenticated, setWasAuthenticated] = useState(false)
  if (query.isSuccess && !wasAuthenticated) setWasAuthenticated(true)

  const status: SessionStatus = useMemo(() => {
    if (query.isPending) return 'loading'
    if (query.isSuccess) return 'authenticated'
    if (isApiError(query.error) && query.error.isUnauthorized) return 'anonymous'
    return 'error'
  }, [query.isPending, query.isSuccess, query.error])

  const expired = status === 'anonymous' && wasAuthenticated
  const preferences = query.data?.preferences ?? null

  const value = useMemo(
    () => ({ status, preferences, expired, refresh }),
    [status, preferences, expired, refresh],
  )

  return <SessionContext value={value}>{children}</SessionContext>
}
